import { UploadOutlined } from '@ant-design/icons';
import { Form, Input } from 'antd';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type {
  ModelClientFieldValue,
  UpdateClientPayload,
} from '@app/api/Client';
import {
  useLazyGetClientByIdQuery,
  useLazyGetClientFieldValuesQuery,
  useSetClientFieldValuesMutation,
  useUpdateClientMutation,
} from '@app/api/Client/api';
import {
  type ModelClientCheckResult,
  useCheckClientForDuplicateMutation,
  useMergeClientsAtSameBranchMutation,
} from '@app/api/ClientsMerge';
import { useLazyGetClientLanguagesQuery } from '@app/api/Dictionaries';
import { useNormalizeUserPhotoMutation } from '@app/api/User/api';
import type {
  ClientDataForm,
  EditClientProps,
} from '@app/components/EditClient/types';
import type { PhoneValidation } from '@app/components/Phone/types';
import type { TagOptionType } from '@app/components/TagsSelect/types';
import {
  DATE_FORMATS,
  FRONT_WARNING,
  SUCCESS,
  UNKNOWN_GENDER_ID,
  WARNING,
} from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import {
  useLazyGetClientUsernamesQuery,
  useSetClientUsernamesMutation,
} from '@app/services/rtkQuery/leads';
import { selectActiveBranchCountryId, useAppSelector } from '@app/store';
import type { NSmallGlobalEntities, NTags } from '@app/types';
import { getContrastYIQ, transformImportedName } from '@app/utils';

import type { InputRef } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import type { MenuProps } from 'antd/lib';
import type { ChangeEvent } from 'react';

const { useForm, useWatch } = Form;

export const useClientData = (props: EditClientProps) => {
  const {
    clientId,
    isInitialized,
    isOpen,
    isLoading,
    hasChanges,
    setHasChanges,
    onConfirm,
    afterSave,
    setIsInitialized,
    setIsLoading,
    setOnConfirm,
  } = props;
  const navigate = useNavigate();
  const [duplicateCheckResult, setDuplicateCheckResult] =
    useState<ModelClientCheckResult | null>(null);
  const [checkClientForDuplicate] = useCheckClientForDuplicateMutation();
  const [mergeClientsAtSameBranch] = useMergeClientsAtSameBranchMutation();
  const [languagesOptions, setLanguagesOptions] = useState<DefaultOptionType[]>(
    []
  );
  const [phonesValidation, setPhonesValidation] = useState<{
    phone1: PhoneValidation;
    phone2: PhoneValidation;
  }>({
    phone1: { isValid: false, errorMessage: null },
    phone2: { isValid: false, errorMessage: null },
  });
  const [tags, setTags] = useState<NTags.ModelTag[]>([]);
  const [tagOptions, setTagOptions] = useState<TagOptionType[]>([]);
  const {
    activeBranchId,
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();

  const [clientFieldDefs, setClientFieldDefs] = useState<
    ModelClientFieldValue[]
  >([]);
  const [savedData, setSavedData] = useState<ClientDataForm | null>(null);
  const [form] = useForm<ClientDataForm>();
  const { setFieldValue, setFieldsValue, submit, setFields } = form;
  const phoneInputRef = useRef<InputRef | null>(null);
  const formValues = useWatch([], form);
  const activeBranchCountry = useAppSelector(selectActiveBranchCountryId);
  const {
    userAccessRights: {
      Clients: { IsTagsChange },
    },
  } = useNewUserAccessRights();

  const [getClientById] = useLazyGetClientByIdQuery();
  const [getClientFieldValues] = useLazyGetClientFieldValuesQuery();
  const [setClientFieldValues] = useSetClientFieldValuesMutation();
  const [updateClient] = useUpdateClientMutation();
  const [getClientLanguages] = useLazyGetClientLanguagesQuery();
  const [normalizeUserPhoto] = useNormalizeUserPhotoMutation();
  const [getClientUsernames] = useLazyGetClientUsernamesQuery();
  const [setClientUsernames] = useSetClientUsernamesMutation();

  const getClientByIdHandler = useCallback(
    async (clientId: number, activeBranchId: number) => {
      if (!activeBranchId) return;
      try {
        const response = await getClientById({
          clientId,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        return response;
      } catch (error) {
        showNotification(error, WARNING);
      }
    },
    [getClientById, showNotification]
  );

  const initUnsavedData = useCallback(
    (data: ClientDataForm) => {
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          setSavedData(data);
          setIsInitialized(true);
          resolve();
        }, 100);
      });
    },
    [setIsInitialized]
  );

  // Значения дополнительных полей загружаются отдельным методом; ошибка тут
  // не должна блокировать редактирование основных данных клиента.
  const loadClientFieldValues = useCallback(
    async (clientId: number, branchId: number) => {
      try {
        const response = await getClientFieldValues({
          clientId,
          ActiveBranchId: branchId,
        }).unwrap();

        return response;
      } catch (error) {
        showNotification(error, WARNING);
        return [];
      }
    },
    [getClientFieldValues, showNotification]
  );

  // Аккаунты в соцсетях — отдельный метод, как и значения доп. полей: ошибка
  // не должна мешать редактировать основные данные клиента.
  const loadClientUsernames = useCallback(
    async (clientId: number, branchId: number) => {
      try {
        const response = await getClientUsernames({
          clientId,
          ActiveBranchId: branchId,
        }).unwrap();

        return response;
      } catch (error) {
        showNotification(error, WARNING);
        return [];
      }
    },
    [getClientUsernames, showNotification]
  );

  const refreshClientFieldDefs = useCallback(async () => {
    if (!clientId || !activeBranchId) return;

    const fieldValues = await loadClientFieldValues(clientId, activeBranchId);
    setClientFieldDefs(fieldValues);
  }, [clientId, activeBranchId, loadClientFieldValues]);

  // Признак важности берём из значений (GetClientFieldValues): важные поля
  // рендерятся отдельной группой сразу под «Основной телефон».
  const importantClientFieldDefs = useMemo(
    () => clientFieldDefs.filter((field) => field.IsImportant),
    [clientFieldDefs]
  );
  const regularClientFieldDefs = useMemo(
    () => clientFieldDefs.filter((field) => !field.IsImportant),
    [clientFieldDefs]
  );

  const getClientLanguagesHandler = useCallback(async () => {
    try {
      const response = await getClientLanguages({
        ActiveBranchId,
      }).unwrap();
      const options = response.map(({ Name, Id }) => ({
        label: Name,
        value: Id,
      }));
      setLanguagesOptions(options);
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [ActiveBranchId, getClientLanguages, showNotification]);

  const initForm = useCallback(async () => {
    setIsInitialized(false);
    if (!clientId || !isOpen || !activeBranchId) return;
    setIsLoading(true);

    await getClientLanguagesHandler();

    const response = await getClientByIdHandler(clientId, activeBranchId);

    if (!response) {
      showNotification(
        'Непредвиденная ошибка при получении данных клиента',
        FRONT_WARNING
      );
      return;
    }

    const fieldValues = await loadClientFieldValues(clientId, activeBranchId);
    setClientFieldDefs(fieldValues);

    // Аккаунты грузятся здесь же, до setFieldsValue: если подставить их
    // позже отдельным эффектом, checkUnsavedData сразу поднимет hasChanges.
    const usernames = await loadClientUsernames(clientId, activeBranchId);

    const {
      NameLast,
      NameFirst,
      NameMiddle,
      Email,
      Phone1CountryId,
      Phone1,
      Phone2,
      Phone2CountryId,
      IsPhoneVerified,
      Gender,
      ClientTags,
      LanguageId,
      DateBirth,
      Comment,
      Photo,
      CompanyName,
      Snils,
      IdentityDocumentType,
      IdentityDocumentNumber,
      RegistrationAddress,
    } = response;

    const tags = (ClientTags ?? []).filter(
      (item) => item !== null && item !== undefined
    );

    const formattedTags: NTags.ModelTag[] = tags.map((tag) => ({
      Color: tag.Color,
      Description: tag.Description,
      IconId: tag.IconId,
      Id: tag.Id,
      IsSelected: tag.IsSelected,
      IsDeleted: tag.IsDeleted,
      IsSystem: tag.IsSystem,
      Name: tag.Name,
      SystemId: tag.SystemId ?? null,
    }));

    setTags(formattedTags);

    const tagOptions = tags.map(({ Id, Name, Color, IconId }) => ({
      value: Id,
      label: Name,
      color: Color ? getContrastYIQ(Color) : undefined,
      backgroundColor: Color ?? undefined,
      icon: IconId ?? undefined,
    }));

    const selectedTags = tags
      .filter(({ IsSelected }) => IsSelected)
      .map(({ Id }) => Id);

    setTagOptions(tagOptions);

    const formPhone1 = Phone1
      ? {
          CountryId: Phone1CountryId || 1,
          Phone: Phone1 || '',
        }
      : {
          CountryId: activeBranchCountry || 1,
          Phone: '',
        };

    const formPhone2 = Phone2
      ? {
          CountryId: Phone2CountryId || 1,
          Phone: Phone2 || '',
        }
      : {
          CountryId: activeBranchCountry || 1,
          Phone: '',
        };

    const data: ClientDataForm = {
      NameLast: NameLast || '',
      NameFirst: NameFirst || '',
      NameMiddle: NameMiddle || '',
      Email: Email || '',
      Phone1: formPhone1,
      Phone2: formPhone2,
      IsPhoneVerified: IsPhoneVerified,
      Gender: Gender || UNKNOWN_GENDER_ID,
      Language: LanguageId || null,
      Tags: selectedTags,
      DateBirth: dayjs(DateBirth).isValid() ? dayjs(DateBirth) : null,
      Comment: Comment || '',
      Photo: Photo || null,
      CompanyName: CompanyName || '',
      Snils,
      IdentityDocumentType,
      IdentityDocumentNumber,
      RegistrationAddress,
      ClientFieldValues: Object.fromEntries(
        fieldValues.map(({ FieldId, Value }) => [String(FieldId), Value ?? ''])
      ),
      ClientUsernames: usernames.map(
        ({ SocialType, Username, IsEditable }) => ({
          SocialType,
          Username: Username ?? '',
          IsEditable,
        })
      ),
    };

    setFieldsValue(data);
    await initUnsavedData(data);

    setIsLoading(false);
  }, [
    activeBranchCountry,
    clientId,
    getClientByIdHandler,
    getClientLanguagesHandler,
    loadClientFieldValues,
    loadClientUsernames,
    isOpen,
    setFieldsValue,
    activeBranchId,
    initUnsavedData,
    showNotification,
    setIsLoading,
    setIsInitialized,
  ]);

  const onFinish = useCallback(
    async (values: ClientDataForm) => {
      if (!clientId) {
        return showNotification('Не указан ID клиента', FRONT_WARNING);
      }

      const {
        NameFirst,
        NameLast,
        NameMiddle,
        Email,
        IsPhoneVerified,
        Language,
        Gender,
        Phone1,
        Phone2,
        DateBirth,
        Comment,
        Tags,
        Photo,
        CompanyName,
        Snils,
        IdentityDocumentType,
        IdentityDocumentNumber,
        RegistrationAddress,
      } = values;

      const { phone1, phone2 } = phonesValidation;

      const mainPhoneError =
        phone1.errorMessage || (!phone1.isValid ? 'Обязательное поле' : '');

      if (mainPhoneError) {
        setFields([{ name: 'Phone1', errors: [mainPhoneError] }]);
        return;
      }

      setIsLoading(true);

      try {
        const checkResult = await checkClientForDuplicate({
          ActiveBranchId,
          data: {
            ClientId: clientId ?? 0,
            ClientPhone: Phone1.Phone,
          },
        }).unwrap();

        if (checkResult.IsDuplicated) {
          setIsLoading(false);
          setDuplicateCheckResult(checkResult);
          return;
        }
      } catch (error) {
        showNotification(error, WARNING);
        setIsLoading(false);
        return;
      }

      const ClientTags: NTags.ModelTag[] = Tags.map((tagId) => {
        const tag = tags.find((item) => item.Id === tagId);
        if (!tag) return null;
        return {
          ...tag,
          IsSelected: true,
        };
      }).filter((tag) => tag !== null);
      const isValidPhone2 = phone2.isValid && !phone2.errorMessage;
      const data: UpdateClientPayload = {
        Id: clientId,
        NameFirst,
        NameLast,
        NameMiddle,
        Email,
        IsPhoneVerified,
        LanguageId: Language,
        Gender: Gender as NSmallGlobalEntities.Gender,
        Phone1: Phone1.Phone,
        Phone1CountryId: Phone1.CountryId,
        Phone2: isValidPhone2 ? Phone2.Phone : null,
        Phone2CountryId: isValidPhone2 ? Phone2.CountryId : null,
        DateBirth: DateBirth?.isValid()
          ? DateBirth?.format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE)
          : null,
        Comment: Comment || '',
        CompanyName: CompanyName || '',
        ClientTags,
        Photo,
        IsHuman: true,
        Snils,
        IdentityDocumentType,
        IdentityDocumentNumber,
        RegistrationAddress,
      };

      try {
        await updateClient({
          ActiveBranchId: activeBranchId,
          data,
        }).unwrap();

        if (clientFieldDefs.length) {
          const FieldValues: ModelClientFieldValue[] = clientFieldDefs.map(
            (def) => ({
              Id: def.Id,
              FieldId: def.FieldId,
              ClientId: clientId,
              FieldName: def.FieldName,
              Value: values.ClientFieldValues?.[String(def.FieldId)] ?? '',
              // важность живёт на определении поля — возвращаем как пришла,
              // иначе сохранение значений сбросило бы её
              IsImportant: def.IsImportant,
            })
          );

          await setClientFieldValues({
            ActiveBranchId,
            data: { ClientId: clientId, FieldValues },
          }).unwrap();
        }

        // SetClientUsernames ждёт ПОЛНЫЙ список каналов, включая
        // нередактируемые: непереданные бэк считает пустыми.
        if (values.ClientUsernames?.length) {
          await setClientUsernames({
            ActiveBranchId,
            Items: values.ClientUsernames.map(
              ({ SocialType, Username, IsEditable }) => ({
                ClientId: clientId,
                SocialType,
                Username: Username ?? '',
                IsEditable,
              })
            ),
          }).unwrap();
        }

        afterSave?.(values);

        await initUnsavedData(values);

        showNotification('Данные клиента успешно сохранены', SUCCESS);
      } catch (error) {
        showNotification(error, WARNING);
      } finally {
        setIsLoading(false);
      }
    },
    [
      clientId,
      phonesValidation,
      setIsLoading,
      showNotification,
      setFields,
      checkClientForDuplicate,
      ActiveBranchId,
      tags,
      updateClient,
      activeBranchId,
      clientFieldDefs,
      afterSave,
      initUnsavedData,
      setClientFieldValues,
      setClientUsernames,
    ]
  );

  const closeDuplicateModalHandler = useCallback(() => {
    setDuplicateCheckResult(null);
  }, []);

  const openExistingClientHandler = useCallback(() => {
    if (!duplicateCheckResult) return;
    navigate(`/clients/card?id=${duplicateCheckResult.ExistingClientId}`);
    setDuplicateCheckResult(null);
  }, [duplicateCheckResult, navigate]);

  const mergeClientsHandler = useCallback(async () => {
    if (!duplicateCheckResult || !clientId) return;
    try {
      const { LeadId } = await mergeClientsAtSameBranch({
        ActiveBranchId,
        data: {
          ClientId1: clientId,
          ClientId2: duplicateCheckResult.ExistingClientId,
        },
      }).unwrap();
      setDuplicateCheckResult(null);
      showNotification('Карточки клиентов объединены', SUCCESS);
      navigate(`/leads/card?leadId=${LeadId}`);
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [
    duplicateCheckResult,
    clientId,
    ActiveBranchId,
    mergeClientsAtSameBranch,
    showNotification,
    navigate,
  ]);

  const uploadAvatar = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files && event.target.files[0];

      if (file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async () => {
          if (typeof reader.result === 'string') {
            const handledResult = reader.result || '';

            if (!handledResult) {
              return showNotification(
                'Произошла ошибка при извлечении содержимого изображения',
                WARNING
              );
            }

            const base64String = handledResult
              .replace('data:', '')
              .replace(/^.+,/, '');

            try {
              const { NormalizedPhoto } = await normalizeUserPhoto({
                ActiveBranchId: activeBranchId,
                data: {
                  InputImage: base64String,
                  InputBytes: null,
                  NormalizedPhoto: null,
                },
              }).unwrap();

              setFieldValue('Photo', NormalizedPhoto);
              showNotification('Изображение установлено', SUCCESS);
            } catch (error) {
              showNotification(handleCatchError(error));
            }
          }
        };
      }
    },
    [activeBranchId, setFieldValue, showNotification, normalizeUserPhoto]
  );

  const avatarDropdownItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: '1',
        label: (
          <div
            onClick={() => !isLoading && phoneInputRef.current?.input?.click()}
            className="unselectable"
          >
            <UploadOutlined style={{ marginRight: '10px' }} />
            Установить фото
            <Input
              ref={phoneInputRef}
              type="file"
              style={{ display: 'none' }}
              id="PhotoInput"
              onChange={uploadAvatar}
              disabled={isLoading}
            />
          </div>
        ),
      },
    ],
    [uploadAvatar, isLoading]
  );

  const handleConfirm = useCallback(() => {
    setOnConfirm?.(submit);
  }, [setOnConfirm, submit]);

  const getFormattedData = useCallback(
    (data: ClientDataForm): ClientDataForm => {
      const formattedData: ClientDataForm = {
        ...data,
        Phone1: {
          CountryId: data?.Phone1?.CountryId || activeBranchCountry || 1,
          Phone: data?.Phone1?.Phone?.replace(/\D/g, '') || '',
        },
        Phone2: {
          CountryId: data?.Phone2?.CountryId || activeBranchCountry || 1,
          Phone: data?.Phone2?.Phone?.replace(/\D/g, '') || '',
        },
      };

      return formattedData;
    },
    [activeBranchCountry]
  );

  const checkUnsavedData = useCallback(() => {
    if (!isInitialized || !savedData || !formValues) return;

    const formattedSavedData: ClientDataForm = getFormattedData(savedData);

    const formattedUnsavedData: ClientDataForm = getFormattedData(formValues);

    const hasChanges = !isEqual(formattedSavedData, formattedUnsavedData);

    setHasChanges(hasChanges);
  }, [formValues, isInitialized, savedData, getFormattedData, setHasChanges]);

  const fallbackName = useMemo(() => {
    if (!formValues) return;
    if (formValues?.Photo) return;

    const { NameFirst, NameLast } = formValues;

    const combinedName =
      NameLast && NameFirst
        ? `${NameLast} ${NameFirst}`
        : NameLast || NameFirst || '';

    return transformImportedName(combinedName);
  }, [formValues]);

  const photoUrl = useMemo(() => {
    if (!formValues?.Photo) return;

    return `data:image/png;base64,${formValues?.Photo}`;
  }, [formValues?.Photo]);

  return {
    form,
    onFinish,
    languagesOptions,
    tagOptions,
    initForm,
    handleConfirm,
    isOpen,
    hasChanges,
    isInitialized,
    checkUnsavedData,
    fallbackName,
    photoUrl,
    onConfirm,
    avatarDropdownItems,
    isLoading,
    setPhonesValidation,
    phonesValidation,
    IsTagsChange,
    duplicateCheckResult,
    closeDuplicateModalHandler,
    openExistingClientHandler,
    mergeClientsHandler,
    clientFieldDefs,
    importantClientFieldDefs,
    regularClientFieldDefs,
    refreshClientFieldDefs,
  };
};
